import * as R from 'ramda';
import { getProductClusters, getProductsInCategory } from 'contexts/plannerConfig';
import { Category, CategoryWidget, ProductListConfig } from 'rangeConfig/@types';
import { sortProducts } from 'products';
import { Product } from '@inter-ikea-storageone/range';
import { BatchSortFn } from 'ducks/products';
import { BatchType, ProductBatch } from 'utils/batch';

export interface CategoryData {
  id: string;
  title: string | undefined;
  widgets: CategoryWidget[];
  productsCluster: Product[][];
}

export const getCategoriesData = (
  productsInGroup: Product[],
  productListConfig: ProductListConfig
): CategoryData[] => {
  const grouped = productListConfig.categories.reduce((acc: CategoryData[], category: Category) => {
    const productsInCategory = getProductsInCategory(productsInGroup, category);
    let products: Product[][];

    const cluster = category.cluster?.length ? category.cluster : productListConfig.cluster;

    if (cluster.length) {
      const clusters = getProductClusters(productsInCategory)(cluster);
      const sortClusteredProductsInDefaultOrder = R.sortWith([
        R.ascend(R.path(['0', 'name'])),
        R.ascend(R.path(['0', 'communicatedDimensions', 'width']))
      ]);
      products = productListConfig.sortClusteredProducts
        ? productListConfig.sortClusteredProducts(clusters)
        : sortClusteredProductsInDefaultOrder(clusters);
    } else {
      const sortOrder = productListConfig.sortOrder;
      products = [sortProducts(productsInCategory, sortOrder)];
    }

    const productsArray = products.length ? products : [[]];
    const categoryWidgets = category.widgets ?? [];

    return [
      ...acc,
      {
        id: category.id,
        title: category.categoryTitle,
        widgets: categoryWidgets,
        productsCluster: productsArray
      }
    ];
  }, []);

  const mergedCategoryProducts = grouped.reduce(
    (acc: CategoryData[], categoryTuple: CategoryData): CategoryData[] => {
      const categoryId = categoryTuple.id;
      const categoryTitle = categoryTuple.title;
      const previousCategoryTuple = acc[acc.length - 1];
      const previousCategoryTupleId = previousCategoryTuple ? previousCategoryTuple.id : undefined;
      if (!categoryTitle && previousCategoryTuple && categoryId === previousCategoryTupleId) {
        // If category doesn't have title and has the same id as previos category then we merge the categories
        const merge: CategoryData = {
          id: previousCategoryTuple.id,
          title: previousCategoryTuple.title,
          widgets: [...previousCategoryTuple.widgets, ...categoryTuple.widgets],
          productsCluster: [
            ...previousCategoryTuple.productsCluster,
            ...categoryTuple.productsCluster
          ]
        };
        acc.pop();
        return [...acc, merge];
      }
      return [...acc, categoryTuple];
    },
    []
  );
  return mergedCategoryProducts;
};

export const sortProductsInCategoriesData = (
  data: CategoryData[],
  productListConfig: ProductListConfig,
  compare: BatchSortFn | string[]
) => {
  const shouldCluster = !!productListConfig.cluster.length;

  const sortClusters = (clusters: Product[][]) =>
    shouldCluster
      ? // toSorted generates errors on chrome < 109 and safari < 15
        clusters.slice().sort(ProductBatch.comparison(compare, BatchType.Cluster))
      : clusters.map(cluster =>
          cluster.slice().sort(ProductBatch.comparison(compare, BatchType.Product))
        );

  return data.map(categoryData => ({
    ...categoryData,
    productsCluster: sortClusters(categoryData.productsCluster)
  }));
};
